import { jsx as _jsx } from "react/jsx-runtime";
import { createRootRouteWithContext, createRoute, Outlet, redirect, } from '@tanstack/react-router';
import { z } from 'zod';
import Loading from 'components/Loading';
import LoginScreen from 'features/auth/screens/LoginScreen';
import ResetPasswordScreen from 'features/auth/screens/ResetPasswordScreen';
import VerifyEmailScreen from 'features/auth/screens/VerifyEmailScreen';
import BlockedScreen from 'features/auth/screens/BlockedScreen';
import AuthActionScreen from 'features/auth/screens/AuthActionScreen';
import ProfileSetupRoute from 'features/profile/ProfileSetupRoute';
import WorkspaceApp from 'WorkspaceApp';
import DashboardRoute from 'routes/_authenticated/dashboardRoute';
import ProjectsRoute from 'routes/_authenticated/projectsRoute';
import AgentsRoute from 'routes/_authenticated/agentsRoute';
import AgentDetailWorkspace from 'components/AgentDetailWorkspace';
import ProjectDetailWorkspace from 'components/ProjectDetailWorkspace';
import LeadsRoute from 'routes/_authenticated/leadsRoute';
import EditorRoute from 'routes/_authenticated/editorRoute';
import { GET_PROJECT_ACTIONS, LIST_PROJECTS_ACTIONS } from 'features/projects/actionNames';
import { needsProfileSetup } from 'profile';
// ---- Root ----
export const rootRoute = createRootRouteWithContext()({
    beforeLoad: ({ context }) => context.auth.whenReady(),
    component: () => _jsx(Outlet, {}),
});
// ---- / (index) ----
const indexRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/',
    beforeLoad: ({ context }) => {
        const s = context.auth.getStatus();
        if (s.kind === 'anonymous')
            throw redirect({ to: '/login' });
        if (s.kind === 'email-unverified')
            throw redirect({ to: '/verify-email' });
        if (s.kind === 'blocked') {
            // Scenarios 3/4 (user present) → send them into the workspace shell which now
            // renders in blocked mode. Only full-exchange failures (no user) fall back to the
            // standalone /blocked screen.
            if (!s.user)
                throw redirect({ to: '/blocked', search: { reason: s.reason } });
            throw redirect({ to: '/dashboard' });
        }
        if (s.kind === 'authenticated') {
            if (needsProfileSetup(s.user))
                throw redirect({ to: '/profile-setup' });
            throw redirect({ to: '/dashboard' });
        }
    },
});
// ---- /auth/action ----
const authActionSearchSchema = z.object({
    mode: z.enum(['verifyEmail', 'resetPassword']),
    oobCode: z.string().min(1),
});
const authActionRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/auth/action',
    validateSearch: authActionSearchSchema,
    component: function AuthAction() {
        const { mode, oobCode } = authActionRoute.useSearch();
        return _jsx(AuthActionScreen, { mode: mode, oobCode: oobCode });
    },
});
// ---- /blocked ----
const blockedSearchSchema = z.object({
    reason: z.enum(['deleted', 'inactive', 'exchange-failed']).optional(),
});
const blockedRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/blocked',
    validateSearch: blockedSearchSchema,
    beforeLoad: ({ context }) => {
        const s = context.auth.getStatus();
        if (s.kind === 'anonymous')
            throw redirect({ to: '/login' });
        if (s.kind === 'authenticated' || s.kind === 'email-unverified')
            throw redirect({ to: '/' });
        // Scenarios 3/4: user is present → in-shell blocked mode covers this case; the
        // standalone /blocked page is only for exchange failures (no user).
        if (s.kind === 'blocked' && s.user)
            throw redirect({ to: '/dashboard' });
    },
    component: function Blocked() {
        const { reason } = blockedRoute.useSearch();
        return _jsx(BlockedScreen, { reason: (reason || 'exchange-failed') });
    },
});
// ---- /verify-email ----
const verifyEmailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/verify-email',
    beforeLoad: ({ context }) => {
        const s = context.auth.getStatus();
        if (s.kind === 'anonymous')
            throw redirect({ to: '/login' });
        if (s.kind === 'blocked')
            throw redirect({ to: '/blocked', search: { reason: s.reason } });
        if (s.kind === 'authenticated')
            throw redirect({ to: '/' });
        // email-unverified — render
    },
    component: VerifyEmailScreen,
});
// ---- /profile-setup ----
const profileSetupRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/profile-setup',
    beforeLoad: ({ context }) => {
        const s = context.auth.getStatus();
        if (s.kind === 'anonymous')
            throw redirect({ to: '/login' });
        if (s.kind === 'blocked')
            throw redirect({ to: '/blocked', search: { reason: s.reason } });
        if (s.kind === 'email-unverified')
            throw redirect({ to: '/verify-email' });
        // authenticated — only allow if profile incomplete
        if (s.kind === 'authenticated') {
            if (!needsProfileSetup(s.user))
                throw redirect({ to: '/dashboard' });
        }
    },
    component: ProfileSetupRoute,
});
// ---- Public layout (authenticated users bounce to /) ----
const publicLayoutRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: 'public',
    beforeLoad: ({ context }) => {
        const s = context.auth.getStatus();
        if (s.kind === 'authenticated' || s.kind === 'email-unverified') {
            throw redirect({ to: '/' });
        }
        if (s.kind === 'blocked') {
            throw redirect({ to: '/blocked', search: { reason: s.reason } });
        }
    },
    component: () => _jsx(Outlet, {}),
});
const loginRoute = createRoute({
    getParentRoute: () => publicLayoutRoute,
    path: '/login',
    component: LoginScreen,
});
const resetPasswordSearchSchema = z.object({
    code: z.string().min(1),
});
const resetPasswordRoute = createRoute({
    getParentRoute: () => publicLayoutRoute,
    path: '/reset-password',
    validateSearch: resetPasswordSearchSchema,
    component: function ResetPassword() {
        const { code } = resetPasswordRoute.useSearch();
        return _jsx(ResetPasswordScreen, { code: code });
    },
});
// ---- Authenticated layout (fully onboarded users only) ----
const authenticatedLayoutRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: 'authenticated',
    beforeLoad: ({ context }) => {
        const s = context.auth.getStatus();
        if (s.kind === 'anonymous')
            throw redirect({ to: '/login' });
        if (s.kind === 'email-unverified')
            throw redirect({ to: '/verify-email' });
        if (s.kind === 'blocked') {
            // Scenarios 3/4: the exchange succeeded and we built a user, but a business rule
            // rejected them. They see the workspace shell with disabled nav + a blocked banner,
            // so they can still reach Settings and Logout. No user means the exchange itself
            // failed — fall back to the full-screen /blocked page.
            if (!s.user)
                throw redirect({ to: '/blocked', search: { reason: s.reason } });
            return { user: s.user, blocked: s.reason };
        }
        if (s.kind === 'authenticated') {
            if (needsProfileSetup(s.user))
                throw redirect({ to: '/profile-setup' });
            return { user: s.user, blocked: null };
        }
        // loading — let the component render; __root.beforeLoad already awaits whenReady
    },
    component: function AuthenticatedLayout() {
        const ctx = authenticatedLayoutRoute.useRouteContext();
        if (!ctx.user)
            return _jsx(Loading, { isVisible: true, title: "" });
        return _jsx(WorkspaceApp, { user: ctx.user, blocked: ctx.blocked ?? null });
    },
});
const dashboardRoute = createRoute({
    getParentRoute: () => authenticatedLayoutRoute,
    path: '/dashboard',
    component: DashboardRoute,
});
/**
 * Per Jira: gate routes by the actual potentialAction the page needs, not by role.
 * Accepts alternative names where Owner and Agent get differently-named actions for
 * the same page. If none is on the user's account, send them to the dashboard.
 */
function requireAction(...names) {
    return ({ context }) => {
        const s = context.auth.getStatus();
        if (s.kind !== 'authenticated')
            return; // outer guards already handle other kinds
        if (!names.some((name) => s.user.potentialActions[name])) {
            throw redirect({ to: '/dashboard' });
        }
    };
}
const projectsRoute = createRoute({
    getParentRoute: () => authenticatedLayoutRoute,
    path: '/projects',
    beforeLoad: requireAction(...LIST_PROJECTS_ACTIONS),
    component: ProjectsRoute,
});
const projectDetailRoute = createRoute({
    getParentRoute: () => authenticatedLayoutRoute,
    path: '/projects/$projectId',
    beforeLoad: requireAction(...GET_PROJECT_ACTIONS),
    component: function ProjectDetail() {
        const { projectId } = projectDetailRoute.useParams();
        return _jsx(ProjectDetailWorkspace, { projectId: projectId });
    },
});
const agentsRoute = createRoute({
    getParentRoute: () => authenticatedLayoutRoute,
    path: '/agents',
    beforeLoad: requireAction('listAgents'),
    component: AgentsRoute,
});
const agentDetailRoute = createRoute({
    getParentRoute: () => authenticatedLayoutRoute,
    path: '/agents/$agentId',
    beforeLoad: requireAction('getAgent'),
    component: function AgentDetail() {
        const { agentId } = agentDetailRoute.useParams();
        return _jsx(AgentDetailWorkspace, { agentId: agentId });
    },
});
const leadsRoute = createRoute({
    getParentRoute: () => authenticatedLayoutRoute,
    path: '/leads',
    component: LeadsRoute,
});
const editorRoute = createRoute({
    getParentRoute: () => authenticatedLayoutRoute,
    path: '/editor',
    component: EditorRoute,
});
// ---- Assemble ----
export const routeTree = rootRoute.addChildren([
    indexRoute,
    authActionRoute,
    blockedRoute,
    verifyEmailRoute,
    profileSetupRoute,
    publicLayoutRoute.addChildren([loginRoute, resetPasswordRoute]),
    authenticatedLayoutRoute.addChildren([
        dashboardRoute,
        projectsRoute,
        projectDetailRoute,
        agentsRoute,
        agentDetailRoute,
        leadsRoute,
        editorRoute,
    ]),
]);
